// @catalog-federation — fingerprints (D17)
// ============================================================================
// The TypeScript twin of libs/federation/Fingerprints.cs. A fingerprint is the
// full 128-hex lowercase SHA-512 over the raw public key — the combined hybrid
// key, or the ES256 SubjectPublicKeyInfo DER of a `cid.jwt` key — shown in
// 8-character groups, two lines of eight. Never truncated: every
// trust-establishing check a person makes is a typed read-back of the whole
// value (docs/cid-governance.md D17), and a shortened display invites checking
// only the part shown.

import { isSha512Hex, sha512Hex } from './json';

export const FINGERPRINT_GROUP_LENGTH = 8;
export const FINGERPRINT_GROUPS_PER_LINE = 8;

/** The D17 fingerprint of a raw public key (C# `Fingerprints.Of`). */
export function publicKeyFingerprint(publicKey: Uint8Array): Promise<string> {
    return sha512Hex(publicKey);
}

/** The sixteen 8-character groups of a fingerprint, for layouts that place
 *  them themselves. Throws unless given a whole lowercase fingerprint. */
export function fingerprintGroups(fingerprint: string): string[] {
    if (!isSha512Hex(fingerprint)) {
        throw new RangeError('a fingerprint is 128 lowercase hex characters (the full SHA-512); refusing to show part of one');
    }
    const groups: string[] = [];
    for (let i = 0; i < fingerprint.length; i += FINGERPRINT_GROUP_LENGTH) {
        groups.push(fingerprint.slice(i, i + FINGERPRINT_GROUP_LENGTH));
    }
    return groups;
}

/**
 * The D17 display, byte for byte what C# `Fingerprints.Format` prints: sixteen
 * groups of eight separated by single spaces, a line break after the eighth.
 * Shown in a monospace block that keeps the line break (`white-space: pre`).
 */
export function formatFingerprint(fingerprint: string): string {
    const groups = fingerprintGroups(fingerprint);
    const lines: string[] = [];
    for (let i = 0; i < groups.length; i += FINGERPRINT_GROUPS_PER_LINE) {
        lines.push(groups.slice(i, i + FINGERPRINT_GROUPS_PER_LINE).join(' '));
    }
    return lines.join('\n');
}

/** What a person typed, whitespace removed and lowercased (C# `Fingerprints.Normalize`). */
export function normalizeFingerprint(typed: string): string {
    return typed.replace(/\s+/g, '').toLowerCase();
}

/**
 * True when `typed` is exactly `fingerprint`, however spaced, broken into
 * lines or cased — the typed read-back (C# `Fingerprints.Matches`). Only a
 * whole fingerprint is ever matched.
 */
export function fingerprintMatches(typed: string, fingerprint: string): boolean {
    return isSha512Hex(fingerprint) && normalizeFingerprint(typed) === fingerprint;
}
